import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CustomTranslateLoader } from '@core/i18n/custom-translate-loader';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Icon } from '@shared/components/icon/icon.enum';
import { ChooseLanguage } from '@shared/models/choose-language';

@Component({
  selector: 'app-choose-language',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    TranslateModule,
  ],
  templateUrl: './choose-language.component.html',
  styleUrls: ['./choose-language.component.scss'],
})
export class ChooseLanguageComponent implements OnInit, AfterViewInit {
  /** Options for the dropdown */
  @Input() options: { value: string; label: string; src?: string }[] = [];
  /** Show flags in dropdown */
  @Input() showFlags?: boolean;
  /** Preselected langauge */
  @Input() defaultLanguage?: string;
  @Input() displayVariant: 'PRIMARY' | 'SECONDARY' = 'PRIMARY';
  /** Renders the component as a dropdown selector or a clickable list. */
  @Input() displayAs: 'selector' | 'list' = 'selector';
  @Input() translationKeys?: string[] = [];
  @Input() fallbackLanguage?: string;
  @Output() selectedLanguage = new EventEmitter<ChooseLanguage>();
  chevron = Icon.Chevron;
  languageForm!: FormGroup;
  selectedLanguageValue: string | undefined;
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('languageSelect', { read: ElementRef })
  languageSelectRef!: ElementRef;

  private inputEl: HTMLInputElement | null = null;
  private dropdownPanelId = `language-select-panel-${Math.random().toString(36).slice(2)}`;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private translateLoader: CustomTranslateLoader,
    private renderer: Renderer2,
    private hostRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.selectedLanguageValue = this.defaultLanguage;

    if (this.displayAs === 'selector') {
      this.languageForm = this.formBuilder.group({
        language: [this.defaultLanguage],
      });

      this.languageForm
        .get('language')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((newLanguage: string) => {
          this.selectedLanguageValue = newLanguage;
          this.onLanguageChange(newLanguage);
        });
    }

    if (this.defaultLanguage) {
      this.onLanguageChange(this.defaultLanguage, true);
    }
  }

  ngAfterViewInit(): void {
    this.applyAttributesToInternalInput();
  }

  onOpen(): void {
    this.applyAttributesToInternalInput();
    if (!this.inputEl) return;

    this.renderer.setAttribute(this.inputEl, 'aria-expanded', 'true');

    const panel = document.querySelector(
      '.ng-dropdown-panel',
    ) as HTMLElement | null;
    if (panel) {
      this.renderer.setAttribute(panel, 'id', this.dropdownPanelId);
      this.renderer.setAttribute(
        this.inputEl,
        'aria-controls',
        this.dropdownPanelId,
      );
    }
  }

  onClose(): void {
    if (!this.inputEl) return;
    this.renderer.setAttribute(this.inputEl, 'aria-expanded', 'false');
    this.renderer.removeAttribute(this.inputEl, 'aria-controls');
  }

  selectLanguage(language: string): void {
    if (this.selectedLanguageValue === language) {
      return; // Avoid redundant processing if the same language is clicked
    }
    this.selectedLanguageValue = language;
    this.onLanguageChange(language);
  }

  onLanguageChange(
    language: string,
    calledWithInitialLanguage: boolean = false,
  ): void {
    this.translateLoader
      .getTranslation(language, this.translationKeys, this.fallbackLanguage)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translation) => {
        this.translateService.setTranslation(language, translation, true);
        this.translateService.use(language);
      });

    this.selectedLanguage.emit({ language, calledWithInitialLanguage });
  }

  private applyAttributesToInternalInput(): void {
    const hostEl: HTMLElement | null =
      this.languageSelectRef?.nativeElement ??
      this.hostRef.nativeElement.querySelector('#language-select');

    if (!hostEl) {
      return;
    }

    const found = hostEl.querySelector<HTMLInputElement>(
      'input[aria-autocomplete], input[type="text"], input[role="combobox"], .ng-select-container input',
    );

    if (found) {
      this.inputEl = found;
      this.renderer.setAttribute(
        this.inputEl,
        'aria-labelledby',
        'language-selector-label',
      );

      this.renderer.setAttribute(this.inputEl, 'autocomplete', 'off');
      this.renderer.setAttribute(this.inputEl, 'aria-expanded', 'false');
    }
  }
}
